// assets/js/api/AuthService.js

import { apiService } from '/assets/js/api/ApiService.js';

class AuthService {
  constructor() {
    // Non usiamo più il token direttamente, ma lo stato dell'utente.
    this.currentUser = null;
    this.authenticationChecked = false; // Flag per sapere se abbiamo già verificato lo stato
  }

  /**
   * Esegue il login. Il backend imposterà un cookie HttpOnly.
   * Il frontend non ha bisogno di gestire il token.
   */
  async login(email, password, remember = false) {
    // La chiamata API ora serve solo per autenticare e ricevere il cookie.
    const response = await apiService.post('/v1/auth/login', { email, password, remember });

    // Dopo il login, possiamo considerare l'utente autenticato.
    // Possiamo opzionalmente caricare il profilo utente qui.
    this.authenticationChecked = true;

    return response;
  }

  /**
   * Esegue il logout. Il backend invaliderà il cookie.
   */
  async logout() {
    try {
      await apiService.post('/v1/auth/logout');
    } catch (error) {
      console.error("Errore durante la chiamata di logout al server, ma procedo con il logout lato client.", error);
    } finally {
      // Pulisce lo stato interno del client
      this.currentUser = null;
      this.authenticationChecked = false;

      console.log("Sessione client pulita. Reindirizzamento al login.");

      // Reindirizza alla pagina di login per completare il logout
      window.location.href = '/login';
    }
  }

  /**
   * Verifica se l'utente è autenticato.
   * Se non abbiamo ancora controllato, chiama l'API per ottenere il profilo.
   * Questo metodo è ora il punto centrale per la verifica dell'autenticazione.
   */
  async isAuthenticated() {
    // Se abbiamo già verificato e abbiamo un utente, è autenticato.
    if (this.authenticationChecked && this.currentUser) {
      return true;
    }

    // Se abbiamo già verificato e non c'è utente, non è autenticato.
    if (this.authenticationChecked && !this.currentUser) {
      return false;
    }

    // Se non abbiamo ancora verificato, proviamo a caricare il profilo.
    // La presenza del cookie HttpOnly permetterà a questa chiamata di funzionare.
    try {
      await this.getUserProfile();
      this.authenticationChecked = true;
      return !!this.currentUser;
    } catch (error) {
      // Se la chiamata fallisce (es. 401), l'utente non è autenticato.
      this.authenticationChecked = true;
      this.currentUser = null;
      return false;
    }
  }

  /**
   * Carica il profilo dell'utente autenticato.
   * Se il profilo è già stato caricato, lo restituisce dalla cache.
   */
  async getUserProfile() {
    if (this.currentUser) {
      return this.currentUser;
    }

    try {
      // Questa chiamata funzionerà solo se il browser invia il cookie HttpOnly.
      const response = await apiService.get('/v1/users/me');
      this.currentUser = response.data;
      return this.currentUser;
    } catch (error) {
      this.currentUser = null;
      // Rilanciamo l'errore così che isAuthenticated() possa gestirlo.
      throw error;
    }
  }

  // --- Gli altri metodi rimangono invariati ---

  async forgotPassword(email) {
    return await apiService.post('/v1/auth/forgot-password', { email });
  }

  async resetPassword(token, password, confirmPassword) {
    return await apiService.post('/v1/auth/reset-password', { token, password, confirmPassword });
  }
}

export const authService = new AuthService();